import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Button, TextField, RadioGroup } from '@mui/material'
import CopyToClipBoard from 'react-copy-to-clipboard'
import { StackItem } from '../layout/StackItem'
import { MedicalInstitutionProps } from 'types/med'
import RadioButtonWithLabel from 'ui/partials/RadioButtonWithLabel'
import type { RadioButtonWithLabelProps } from 'ui/partials/RadioButtonWithLabel'

const useStyles = makeStyles()((theme: Theme) => ({
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
  },
  buttonTextLabel: {
    padding: theme.spacing(2, 0),
  },
  displayLabel: {
    display: 'inline-block',
    flexShrink: 0,
  },
  previewWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(-2),
    minHeight: 104,
    paddingBottom: theme.spacing(3),
  },
  copyButtonWrapper: {
    textAlign: 'right',
    padding: theme.spacing(1, 0),
  },
}))

export const createLinkBuildScriptUrl: (data: {
  filePath: string
  id: string
  text?: string
  settings?: { type?: string; layout?: string; target?: string }
}) => string = ({ filePath, id, text = '', settings }) => {
  const searchParams = new URLSearchParams()
  searchParams.append('id', id)
  searchParams.append('text', text)
  settings &&
    Object.entries(settings).forEach(([name, value]) => {
      value && searchParams.append(name, value)
    })
  return `${filePath}?${searchParams.toString()}`
}

const buttonTypeSettings: RadioButtonWithLabelProps<'グリーン' | 'ホワイト'>[] =
  [
    { label: 'グリーン', value: '' },
    { label: 'ホワイト', value: 'light' },
  ]

const buttonLayoutSettings: RadioButtonWithLabelProps<
  '左上' | '左下' | '右上' | '右下'
>[] = [
  { label: '左上', value: '' },
  { label: '左下', value: 'bottom-left' },
  { label: '右上', value: 'top-right' },
  { label: '右下', value: 'bottom-right' },
]

export const LinkBuilder: React.FC<{
  med: MedicalInstitutionProps
  filePath: string
}> = ({ med, filePath }) => {
  const { t } = useTranslation()

  const { classes } = useStyles()

  const previewButtonTargetId = 'yq-script-build-script-target'
  const scriptTagId = 'yq-script-build-script-tag'

  const [text, setText] = useState<string>(t('オンライン診療予約'))
  const [type, setType] = useState<string>('')
  const [layout, setLayout] = useState<string>('')
  const [scriptTag, setScriptTag] = useState<string>('')
  const [localScriptUrl, setLocalScriptUrl] = useState<string>('')

  useEffect(() => {
    const url = createLinkBuildScriptUrl({
      filePath,
      // TODO: サーバーからinstitution_access_keyをもらうように修正するべき
      id: med.form_url?.split('/reception/')[1] || '',
      text,
      settings: {
        type,
        layout,
      },
    })
    setScriptTag(`<script async src="${url}"></script>`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, type, layout])

  useEffect(() => {
    const url = createLinkBuildScriptUrl({
      filePath,
      // TODO: サーバーからinstitution_access_keyをもらうように修正するべき
      id: med.form_url?.split('/reception/')[1] || '',
      text: text || '　',
      settings: {
        type,
      },
    })
    setLocalScriptUrl(`${url}&target=${previewButtonTargetId}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, type])

  useEffect(() => {
    const targetTag = document.getElementById(previewButtonTargetId)
    if (targetTag) {
      while (targetTag.firstChild) {
        targetTag.removeChild(targetTag.firstChild)
      }
    }
    const head = document.getElementsByTagName('head')[0] as HTMLElement
    const scriptEl = document.createElement('script')
    scriptEl.type = 'text/javascript'
    scriptEl.src = localScriptUrl
    scriptEl.async = true
    scriptEl.id = scriptTagId
    head.appendChild(scriptEl)
    return () => {
      const tag = document.getElementById(scriptTagId)
      tag && tag.remove()
    }
  }, [localScriptUrl])

  return (
    <>
      <StackItem label={t('プレビュー')} />
      <div className={classes.previewWrapper} id={previewButtonTargetId} />
      <StackItem
        label={
          <span className={classes.buttonTextLabel}>{t('ボタン表記')}</span>
        }
        alignTop
      >
        <TextField
          placeholder={t(
            '「オンライン診療予約」などのテキストを入力してください'
          )}
          variant="outlined"
          value={text}
          fullWidth
          onChange={(e) => setText(e.target.value)}
          helperText={t('全角16文字までを推奨')}
        />
      </StackItem>
      <StackItem label={t('カラー')}>
        <RadioGroup
          className={classes.radioGroup}
          onChange={(e) => setType(e.target.value)}
          value={type}
        >
          {buttonTypeSettings.map((s) => (
            <RadioButtonWithLabel
              key={`${s.label}${s.value}`}
              value={s.value}
              label={t(s.label)}
            />
          ))}
        </RadioGroup>
      </StackItem>
      <StackItem
        label={
          <QuestionTooltipWithLabel label={t('表示位置')}>
            {t('ボタンはフロートして画面に固定されます。')}
          </QuestionTooltipWithLabel>
        }
      >
        <RadioGroup
          className={classes.radioGroup}
          onChange={(e) => setLayout(e.target.value)}
          value={layout}
        >
          {buttonLayoutSettings.map((s) => (
            <RadioButtonWithLabel
              key={`${s.label}${s.value}`}
              value={s.value}
              label={t(s.label)}
            />
          ))}
        </RadioGroup>
      </StackItem>
      <StackItem
        label={
          <span className={classes.displayLabel}>
            <QuestionTooltipWithLabel label={t('貼り付けコード')}>
              {t('<head>タグと</head>タグの間に貼り付けてください。')}
            </QuestionTooltipWithLabel>
          </span>
        }
        alignTop
      >
        <TextField variant="outlined" value={scriptTag} fullWidth multiline />
        <div className={classes.copyButtonWrapper}>
          <CopyToClipBoard text={scriptTag}>
            <Button color="primary" variant="contained" disabled={!text}>
              {t('コピー')}
            </Button>
          </CopyToClipBoard>
        </div>
      </StackItem>
    </>
  )
}

export default LinkBuilder
